import { GraphQlRequestService } from '../../../api/graphql-request-service';
import { Granularity } from '../../common/components/timeslider/types';
import { Domains } from '../../constants';
import { DataTypes } from '../../types';
import { getMomentDate } from '../../utils-date';
import { Hasher } from '../hasher';
import { HasDependencies, hashString, Role } from '../types';
import { handleGQLErrors } from '../utils';
import { TimeRangesMap } from './types';

export interface TimeRangesService extends HasDependencies {
  getTimeRanges: () => Promise<Partial<TimeRangesMap> | null>;
}

export class BackendTimeRangesService implements TimeRangesService {
  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly domain: Domains,
    readonly simulateRole: Role | null
  ) {}

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      this.domain,
      Hasher.hash(this.simulateRole),
    ]),
  ];

  getTimeRanges = async (): Promise<Partial<TimeRangesMap> | null> => {
    const result = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.availableTimeRanges({
        domain: this.domain,
        simulateRole: this.simulateRole?.id ?? null,
      })
    );
    return result.availableTimeranges
      ? Object.fromEntries(
          result.availableTimeranges.flatMap((atr) => {
            const monthlyTimeRange =
              atr.availableTimerange.calendarYearMonthly?.map((d) =>
                getMomentDate(d).toDate()
              );
            const yearlyTimeRange = atr.availableTimerange.calendarYearYearly;
            const finYearlyTimeRange =
              atr.availableTimerange.financialYearYearly;
            const finQuarterlyTimeRange =
              atr.availableTimerange.financialYearQuarterly;
            if (
              monthlyTimeRange &&
              yearlyTimeRange &&
              finYearlyTimeRange &&
              finQuarterlyTimeRange
            ) {
              return [
                [
                  atr.dataType as unknown as DataTypes,
                  {
                    [Granularity.MONTH]: {
                      min: monthlyTimeRange.first(),
                      max: monthlyTimeRange.last(),
                      all: monthlyTimeRange,
                    },
                    [Granularity.YEAR]: {
                      min: yearlyTimeRange.first(),
                      max: yearlyTimeRange.last(),
                      all: yearlyTimeRange,
                    },
                    [Granularity.FINYEAR]: {
                      min: finYearlyTimeRange.first(),
                      max: finYearlyTimeRange.last(),
                      all: finYearlyTimeRange,
                    },
                    [Granularity.FINQUARTER]: {
                      min: finQuarterlyTimeRange.first(),
                      max: finQuarterlyTimeRange.last(),
                      all: finQuarterlyTimeRange,
                    },
                  },
                ],
              ];
            } else {
              return [];
            }
          })
        )
      : null;
  };
}
